import { t as tr } from "@/lib/i18n";
import { WriteOnly } from "@/components/auth/write-only";
import { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Plus, Tag as TagIcon, Send, Rocket } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { ListView } from "@/components/list-view";
import {
  Button,
  Checkbox,
  cn,
  Input,
  Label,
  PageHeader,
  Switch,
  Textarea,
} from "@fox1ck-org/ui";
import { PageState } from "@fox1ck-org/ui";
import { failureText } from "@/lib/errors";
import { useTableParams } from "@/lib/hooks/use-table-params";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@fox1ck-org/ui";
import { EntityDrawer } from "@fox1ck-org/ui";
import { MonacoSnippetEditor } from "@/components/canonical/monaco-snippet-editor";
import { ResourceKind } from "@gen/tracker/v1/common_pb";
import {
  TagManagerSnippet,
  TagManagerSnippetKind,
  TagManagerPlacement,
  TagManagerSnippetStatus,
  TagManagerScope,
  TagManagerDestination,
  TagManagerDestinationConfig,
  Ga4MpConfig,
  TiktokEventsConfig,
} from "@gen/tracker/v1/tag_manager_pb";
import { Condition, ExpressionCondition } from "@gen/tracker/v1/rule_pb";
import {
  useAllSnippets,
  useCreateSnippet,
  useUpdateSnippet,
  useDeleteSnippet,
  usePublishSnippet,
  useTestFireDestination,
  scopeKindLabel,
  snippetKindLabel,
  snippetPlacementLabel,
  SNIPPET_SCOPE_KINDS,
  snippetStatusLabel,
  destinationLabel,
  DESTINATION_OPTIONS,
  CONSENT_CATEGORIES,
} from "@/lib/services/tag-manager-service";
import {
  tagManagerSnippetSchema,
  type TagManagerSnippetFormValues,
} from "@/lib/schemas/tag-manager-schema";
import { ResourcePicker } from "@/pages/tag-manager/resource-picker";

const KIND_OPTIONS = [
  TagManagerSnippetKind.GTM,
  TagManagerSnippetKind.PIXEL,
  TagManagerSnippetKind.POSTBACK,
  TagManagerSnippetKind.CUSTOM_JS,
  TagManagerSnippetKind.CUSTOM_HTML,
];

const PLACEMENT_OPTIONS = [
  TagManagerPlacement.HEAD,
  TagManagerPlacement.BODY_TOP,
  TagManagerPlacement.BODY_BOTTOM,
  TagManagerPlacement.BEFORE_REDIRECT,
];

function defaultFormValues(): TagManagerSnippetFormValues {
  return {
    id: undefined,
    name: "",
    kind: TagManagerSnippetKind.CUSTOM_JS,
    scope: { kind: ResourceKind.WORKSPACE, resourceId: "" },
    placement: TagManagerPlacement.BODY_BOTTOM,
    code: "",
    enabled: false,
    conditionsExpression: "",
    consentRequired: [],
    destination: TagManagerDestination.CUSTOM_HTTP,
    destinationConfig: {},
    fieldMappingsJson: "[]",
  };
}

function destinationConfigToFormMap(s: TagManagerSnippet): Record<string, string> {
  const c = s.destinationConfig?.config;
  if (!c) return {};
  switch (c.case) {
    case "ga4Mp":
      return {
        measurement_id: c.value.measurementId,
        api_secret_ref: c.value.apiSecretRef,
        event_name: c.value.eventName,
      };
    case "tiktokEvents":
      return {
        pixel_id: c.value.pixelId,
        access_token_ref: c.value.accessTokenRef,
        event_name: c.value.eventName,
      };
    default:
      return {};
  }
}

function destinationConfigFromFormMap(
  destination: TagManagerDestination,
  cfg: Record<string, string>,
): TagManagerDestinationConfig | undefined {
  switch (destination) {
    case TagManagerDestination.GA4_MP:
      return new TagManagerDestinationConfig({
        config: {
          case: "ga4Mp",
          value: new Ga4MpConfig({
            measurementId: cfg.measurement_id ?? "",
            apiSecretRef: cfg.api_secret_ref ?? "",
            eventName: cfg.event_name ?? "",
          }),
        },
      });
    case TagManagerDestination.TIKTOK_EVENTS:
      return new TagManagerDestinationConfig({
        config: {
          case: "tiktokEvents",
          value: new TiktokEventsConfig({
            pixelId: cfg.pixel_id ?? "",
            accessTokenRef: cfg.access_token_ref ?? "",
            eventName: cfg.event_name ?? "",
          }),
        },
      });
    default:
      return undefined;
  }
}

function snippetToFormValues(s: TagManagerSnippet): TagManagerSnippetFormValues {
  return {
    id: s.id,
    name: s.name,
    kind: s.kind,
    scope: {
      kind: s.scope?.kind ?? ResourceKind.WORKSPACE,
      resourceId: s.scope?.resourceId ?? "",
    },
    placement: s.placement,
    code: s.code,
    enabled: s.enabled,
    conditionsExpression:
      s.conditions?.kind.case === "expression" ? s.conditions.kind.value.source : "",
    consentRequired: [...s.consentRequired],
    destination: s.destination || TagManagerDestination.CUSTOM_HTTP,
    destinationConfig: destinationConfigToFormMap(s),
    fieldMappingsJson: s.fieldMappingsJson || "[]",
  };
}

function formValuesToSnippet(values: TagManagerSnippetFormValues, base?: TagManagerSnippet): TagManagerSnippet {
  const snippet = base ? base.clone() : new TagManagerSnippet();
  if (values.id) snippet.id = values.id;
  snippet.name = values.name.trim();
  snippet.kind = values.kind as TagManagerSnippetKind;
  snippet.scope = new TagManagerScope({
    kind: values.scope.kind as ResourceKind,
    resourceId: values.scope.kind === ResourceKind.WORKSPACE ? "" : values.scope.resourceId ?? "",
  });
  snippet.placement = values.placement as TagManagerPlacement;
  snippet.code = values.code;
  snippet.enabled = values.enabled;
  const expr = values.conditionsExpression.trim();
  snippet.conditions = expr
    ? new Condition({
        kind: { case: "expression", value: new ExpressionCondition({ source: expr }) },
      })
    : undefined;
  snippet.consentRequired = [...values.consentRequired];
  // POSTBACK kind owns the destination/field-mappings surface. Every other
  // kind has the wire fields reset so authoring noise doesn't leak.
  if (snippet.kind === TagManagerSnippetKind.POSTBACK) {
    snippet.destination = values.destination as TagManagerDestination;
    snippet.destinationConfig = destinationConfigFromFormMap(
      snippet.destination,
      values.destinationConfig,
    );
    snippet.fieldMappingsJson = values.fieldMappingsJson?.trim() || "[]";
  } else {
    snippet.destination = TagManagerDestination.UNSPECIFIED;
    snippet.destinationConfig = undefined;
    snippet.fieldMappingsJson = "";
  }
  return snippet;
}

export default function TagManagerListPage() {
  const params = useTableParams({ defaultPageSize: 25, defaultSort: [{ id: "name", desc: false }] });
  const scopeFilter = (Number(params.getFilter("scope")) || ResourceKind.UNSPECIFIED) as ResourceKind;
  const enabledOnly = params.getFilter("enabled") === "1";
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<TagManagerSnippet | null>(null);

  const list = useAllSnippets({ scopeKind: scopeFilter, enabledOnly });
  const createMutation = useCreateSnippet();
  const updateMutation = useUpdateSnippet();
  const deleteMutation = useDeleteSnippet();
  const publishMutation = usePublishSnippet();
  const testFireMutation = useTestFireDestination();

  const form = useForm<TagManagerSnippetFormValues>({
    resolver: zodResolver(tagManagerSnippetSchema) as never,
    defaultValues: defaultFormValues(),
    mode: "onSubmit",
  });

  useEffect(() => {
    if (drawerOpen) {
      form.reset(editing ? snippetToFormValues(editing) : defaultFormValues());
    }
  }, [drawerOpen, editing, form]);

  const columns = useMemo<ColumnDef<TagManagerSnippet>[]>(
    () => [
      {
        id: "name",
        header: tr("flows.col.name"),
        accessorKey: "name",
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-foreground">{row.original.name}</span>
            <span className="text-xs text-muted-foreground">{row.original.id}</span>
          </div>
        ),
        size: 320,
      },
      {
        id: "kind",
        header: tr("param.kind"),
        accessorFn: (e) => e.kind,
        cell: ({ row }) => (
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs uppercase tracking-wide text-muted-foreground">
            {snippetKindLabel(row.original.kind)}
          </span>
        ),
        size: 120,
      },
      {
        id: "scope",
        header: tr("tpl.scope"),
        accessorFn: (e) => e.scope?.kind ?? ResourceKind.UNSPECIFIED,
        cell: ({ row }) => (
          <span className="text-xs">
            {scopeKindLabel(row.original.scope?.kind ?? ResourceKind.UNSPECIFIED) ?? "—"}
          </span>
        ),
        size: 130,
      },
      {
        id: "placement",
        header: tr("tm.placement"),
        accessorFn: (e) => e.placement,
        cell: ({ row }) => (
          <span className="text-xs">{snippetPlacementLabel(row.original.placement)}</span>
        ),
        size: 140,
      },
      {
        id: "status",
        header: tr("ws.status"),
        accessorFn: (e) => e.status,
        cell: ({ row }) => {
          const s = row.original.status;
          const isPublished = s === TagManagerSnippetStatus.PUBLISHED;
          const isDraft = s === TagManagerSnippetStatus.DRAFT;
          return (
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
                  isPublished && "bg-ok/15 text-ok",
                  isDraft && "bg-warn/15 text-warn",
                  !isPublished && !isDraft && "bg-muted text-muted-foreground",
                )}
              >
                {snippetStatusLabel(s)}
              </span>
              {row.original.version > 0 ? (
                <span className="text-xs tabular-nums text-muted-foreground">
                  v{row.original.version}
                </span>
              ) : null}
            </div>
          );
        },
        size: 140,
      },
      {
        id: "enabled",
        header: tr("insp.enabled"),
        accessorFn: (e) => (e.enabled ? 1 : 0),
        cell: ({ row }) => (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide",
              row.original.enabled
                ? "bg-ok/15 text-ok"
                : "bg-muted text-muted-foreground",
            )}
          >
            {row.original.enabled ? tr("rules.on") : tr("rules.off")}
          </span>
        ),
        size: 90,
      },
      {
        id: "updated",
        header: tr("tm.updated"),
        accessorFn: (e) => e.updatedAt,
        cell: ({ row }) => (
          <span className="text-xs tabular-nums text-muted-foreground">
            {row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleString("ru-RU") : "—"}
          </span>
        ),
        size: 200,
      },
    ],
    [],
  );

  function openCreate() {
    setEditing(null);
    setDrawerOpen(true);
  }

  function openEdit(snippet: TagManagerSnippet) {
    setEditing(snippet);
    setDrawerOpen(true);
  }

  const submit = form.handleSubmit(async (values) => {
    try {
      if (editing) {
        const updated = formValuesToSnippet(values, editing);
        await updateMutation.mutateAsync(updated);
        toast.success(tr("tm.saved"));
      } else {
        const created = formValuesToSnippet(values);
        await createMutation.mutateAsync(created);
        toast.success(tr("tm.created"));
      }
      setDrawerOpen(false);
      setEditing(null);
    } catch (err) {
      // BE FieldError surfaces via X-Field header; for now we surface the
      // human message and rely on the BE to mark the bad field in a later iteration.
      toast.error(err instanceof Error ? err.message : tr("ws.saveFailed"));
    }
  });

  async function onDelete() {
    if (!editing) return;
    try {
      await deleteMutation.mutateAsync(editing.id);
      toast.success(tr("tm.deleted"));
      setDrawerOpen(false);
      setEditing(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tr("rule.ed.deleteFailed"));
    }
  }

  async function onPublish() {
    if (!editing) {
      toast.info(tr("tm.saveFirstPublish"));
      return;
    }
    try {
      await publishMutation.mutateAsync({ id: editing.id });
      toast.success(tr("tm.published"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tr("tm.publishFailed"));
    }
  }

  async function onTestFire() {
    // The button only renders when kind===POSTBACK; we still gate on editing
    // because TestFireDestination needs a persisted snippet (it dispatches
    // through the live destination registry).
    if (!editing) {
      toast.info(tr("tm.saveFirstTest"));
      return;
    }
    try {
      const res = await testFireMutation.mutateAsync({ snippetId: editing.id });
      if (res.ok) {
        toast.success(tr("tm.testSent", { detail: res.detail || `HTTP ${res.httpStatus}` }));
      } else {
        toast.error(tr("tm.testFailed", { detail: res.detail || `HTTP ${res.httpStatus}` }));
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tr("tm.testError"));
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-col gap-3 px-5 pb-3 pt-4">
        <PageHeader
          icon={<TagIcon className="size-4" />}
          title={tr("tm.title")}
          subtitle={tr("tm.subtitle")}
          actions={
            <WriteOnly>
              <Button variant="primary" onClick={openCreate}>
                <Plus className="size-4" /> {tr("tm.new")}
              </Button>
            </WriteOnly>
          }
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 pb-5">
        {list.isError ? (
          <div data-testid="list-error">
            <PageState
              mode="error"
              title={tr("tm.loadError")}
              error={failureText(list.error)}
              retryLabel={tr("lib.common.retry")}
              retry={() => list.refetch()}
            />
          </div>
        ) : (
          <ListView
            columns={columns}
            data={list.data?.items ?? []}
            getRowId={(row) => row.id}
            loading={list.isLoading}
            clientPagination
            sorting={params.sorting}
            onSortingChange={params.onSortingChange}
            pagination={{
              pageIndex: params.pageIndex,
              pageSize: params.pageSize,
              onPageIndexChange: params.setPageIndex,
              onPageSizeChange: params.setPageSize,
              pageSizeOptions: params.pageSizeOptions,
            }}
            onRowClick={openEdit}
            toolbarLeft={
              <>
                <Select
                  value={String(scopeFilter)}
                  onValueChange={(v) =>
                    params.setFilter("scope", Number(v) === ResourceKind.UNSPECIFIED ? null : v)
                  }
                >
                  <SelectTrigger className="h-8 min-w-45" data-testid="scope-filter" aria-label={tr("tpl.scope")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(ResourceKind.UNSPECIFIED)}>{tr("tm.allScopes")}</SelectItem>
                    {SNIPPET_SCOPE_KINDS.map((k) => (
                      <SelectItem key={k} value={String(k)}>
                        {scopeKindLabel(k)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Switch
                    id="enabled-only"
                    checked={enabledOnly}
                    onCheckedChange={(v) => params.setFilter("enabled", v ? "1" : null)}
                    data-testid="enabled-only-switch"
                  />
                  {tr("tm.enabledOnly")}
                </label>
              </>
            }
            emptyState={
              <div data-testid="empty-state">
                <PageState mode="empty" title={tr("tm.empty")} />
              </div>
            }
          />
        )}
      </div>

      <EntityDrawer
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) setEditing(null);
        }}
        title={editing ? tr("tm.edit") : tr("tm.new")}
        description={tr("tm.desc")}
        width="2xl"
        primaryAction={{
          label: editing ? tr("rule.ed.save") : tr("rule.ed.create"),
          onClick: submit,
          loading: createMutation.isPending || updateMutation.isPending,
        }}
        destructiveAction={
          editing
            ? {
                label: deleteMutation.isPending ? tr("tm.deleting") : tr("rule.ed.delete"),
                onClick: onDelete,
              }
            : undefined
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="space-y-4"
          data-testid="snippet-form"
        >
          <div className="space-y-1">
            <Label textCase="none" htmlFor="snip-name">{tr("flows.col.name")}</Label>
            <Input id="snip-name" font="sans" {...form.register("name")} data-testid="snip-name" />
            {form.formState.errors.name ? (
              <p className="text-xs text-destructive" data-testid="err-name">
                {form.formState.errors.name.message}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label textCase="none">{tr("param.kind")}</Label>
              <Controller
                control={form.control}
                name="kind"
                render={({ field }) => (
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => field.onChange(Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {KIND_OPTIONS.map((k) => (
                        <SelectItem key={k} value={String(k)}>
                          {snippetKindLabel(k)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1">
              <Label textCase="none">{tr("tpl.scope")}</Label>
              <Controller
                control={form.control}
                name="scope.kind"
                render={({ field }) => (
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => {
                      field.onChange(Number(v));
                      // Reset resource_id whenever the scope flips so a
                      // stale id from a different kind cannot leak through.
                      form.setValue("scope.resourceId", "");
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SNIPPET_SCOPE_KINDS.map((s) => (
                        <SelectItem key={s} value={String(s)}>
                          {scopeKindLabel(s)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1">
              <Label textCase="none">{tr("tm.placement")}</Label>
              <Controller
                control={form.control}
                name="placement"
                render={({ field }) => (
                  <Select
                    value={String(field.value)}
                    onValueChange={(v) => field.onChange(Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLACEMENT_OPTIONS.map((p) => (
                        <SelectItem key={p} value={String(p)}>
                          {snippetPlacementLabel(p)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.placement ? (
                <p className="text-xs text-destructive">{form.formState.errors.placement.message}</p>
              ) : null}
            </div>
          </div>

          <Controller
            control={form.control}
            name="scope.resourceId"
            render={({ field }) => {
              const scopeKind = form.watch("scope.kind") as ResourceKind;
              if (scopeKind === ResourceKind.WORKSPACE) return <></>;
              return (
                <div className="space-y-1">
                  <Label textCase="none">{tr("tm.target")}</Label>
                  <ResourcePicker
                    scopeKind={scopeKind}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                  />
                  {form.formState.errors.scope?.resourceId ? (
                    <p className="text-xs text-destructive" data-testid="err-resource">
                      {form.formState.errors.scope.resourceId.message}
                    </p>
                  ) : null}
                </div>
              );
            }}
          />

          <div className="space-y-1">
            <Label textCase="none">{tr("tm.conditions")}</Label>
            <Input
              placeholder={tr("tm.conditions.ph")}
              {...form.register("conditionsExpression")}
              data-testid="snip-conditions"
            />
            <p className="text-xs text-muted-foreground">
              {tr("tm.conditions.hint")}
            </p>
          </div>

          <div className="space-y-1">
            <Label textCase="none">{tr("tm.code")}</Label>
            <Controller
              control={form.control}
              name="code"
              render={({ field }) => (
                <MonacoSnippetEditor
                  kind={kindToMockShape(form.watch("kind") as TagManagerSnippetKind)}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2">
            <div>
              <Label textCase="none" htmlFor="snip-enabled">{tr("insp.enabled")}</Label>
              <p className="text-xs text-muted-foreground">
                {tr("tm.enabled.hint")}
              </p>
            </div>
            <Controller
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <Switch
                  id="snip-enabled"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="snip-enabled"
                />
              )}
            />
          </div>

          {/* Consent Mode v2 — fire only when every checked category is "granted" in the request state. */}
          <div className="space-y-2 rounded-md border border-border bg-muted/40 px-3 py-2">
            <div>
              <Label textCase="none">{tr("tm.consent")}</Label>
              <p className="text-xs text-muted-foreground">
                {tr("tm.consent.hint")}
              </p>
            </div>
            <Controller
              control={form.control}
              name="consentRequired"
              render={({ field }) => (
                <div className="grid grid-cols-2 gap-1.5">
                  {CONSENT_CATEGORIES.map((cat) => (
                    <label
                      key={cat}
                      className="flex items-center gap-2 text-xs text-foreground"
                    >
                      <Checkbox
                        checked={field.value.includes(cat)}
                        onCheckedChange={(checked) => {
                          if (checked) field.onChange([...field.value, cat]);
                          else field.onChange(field.value.filter((c) => c !== cat));
                        }}
                        data-testid={`consent-${cat}`}
                      />
                      <span className="font-mono">{cat}</span>
                    </label>
                  ))}
                </div>
              )}
            />
          </div>

          {/* POSTBACK destination sub-form. Only POSTBACK kind has a server-side
              firing path; every other kind injects code into the browser, so
              the destination dropdown stays hidden. */}
          {(form.watch("kind") as TagManagerSnippetKind) === TagManagerSnippetKind.POSTBACK ? (
            <DestinationSection control={form.control} />
          ) : null}

          {editing ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
              <div className="text-xs text-muted-foreground">
                {editing.status === TagManagerSnippetStatus.PUBLISHED
                  ? tr("tm.publishedV", { v: editing.version })
                  : tr("tm.draftV", { v: editing.version || 1 })}
              </div>
              <div className="ml-auto flex items-center gap-2">
                {(form.watch("kind") as TagManagerSnippetKind) === TagManagerSnippetKind.POSTBACK ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="gap-1"
                    disabled={testFireMutation.isPending}
                    onClick={onTestFire}
                    data-testid="test-fire-btn"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {testFireMutation.isPending ? tr("tm.sending") : tr("tm.sendTest")}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="gap-1"
                  disabled={publishMutation.isPending}
                  onClick={onPublish}
                  data-testid="publish-btn"
                >
                  <Rocket className="h-3.5 w-3.5" />
                  {publishMutation.isPending ? tr("tm.publishing") : tr("tm.publish")}
                </Button>
              </div>
            </div>
          ) : null}
        </form>
      </EntityDrawer>
    </div>
  );
}

// DestinationSection renders the destination dropdown + adapter-specific
// config inputs + the field-mappings JSON editor. Field config keys mirror
// the BE proto exactly (measurement_id, api_secret_ref, pixel_id, etc.) so
// what the FE writes is what the BE persists, no translation layer.
function DestinationSection({ control }: { control: ReturnType<typeof useForm<TagManagerSnippetFormValues>>["control"] }) {
  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/40 px-3 py-2">
      <div>
        <Label textCase="none">{tr("tm.dest")}</Label>
        <p className="text-xs text-muted-foreground">
          {tr("tm.dest.hint")} <code>vibe/prod/vibe-tracker/destinations/&lt;id&gt;</code>.
        </p>
      </div>
      <Controller
        control={control}
        name="destination"
        render={({ field }) => (
          <Select
            value={String(field.value)}
            onValueChange={(v) => field.onChange(Number(v))}
          >
            <SelectTrigger data-testid="destination-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DESTINATION_OPTIONS.map((d) => (
                <SelectItem key={d} value={String(d)}>
                  {destinationLabel(d)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />

      <Controller
        control={control}
        name="destinationConfig"
        render={({ field }) => (
          <DestinationConfigFields
            destination={control._formValues.destination as TagManagerDestination}
            value={field.value}
            onChange={field.onChange}
          />
        )}
      />

      <div className="space-y-1">
        <Label textCase="none">{tr("tm.mappings")}</Label>
        <p className="text-xs text-muted-foreground">
          {tr("tm.mappings.array")} <code>{`{"destination_field":"<dst>","source_macro":"{{macro}}"}`}</code>.{" "}
          {tr("tm.mappings.hint")}
        </p>
        <Controller
          control={control}
          name="fieldMappingsJson"
          render={({ field, fieldState }) => (
            <>
              <Textarea
                mono
                rows={4}
                spellCheck={false}
                className="text-xs"
                value={field.value}
                onChange={field.onChange}
                data-testid="field-mappings"
                placeholder='[{"destination_field":"em","source_macro":"{{user.email}}"}]'
              />
              {fieldState.error ? (
                <p className="text-xs text-destructive">{fieldState.error.message}</p>
              ) : null}
            </>
          )}
        />
      </div>
    </div>
  );
}

interface DestinationConfigFieldsProps {
  destination: TagManagerDestination;
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}

function DestinationConfigFields({ destination, value, onChange }: DestinationConfigFieldsProps) {
  function setField(k: string, v: string) {
    onChange({ ...value, [k]: v });
  }
  switch (destination) {
    case TagManagerDestination.GA4_MP:
      return (
        <div className="grid grid-cols-3 gap-2">
          <LabeledInput label="measurement_id" value={value.measurement_id ?? ""} onChange={(v) => setField("measurement_id", v)} placeholder="G-XXXXXX" />
          <LabeledInput label="api_secret_ref" value={value.api_secret_ref ?? ""} onChange={(v) => setField("api_secret_ref", v)} placeholder="vibe/prod/…/<id>" />
          <LabeledInput label="event_name" value={value.event_name ?? ""} onChange={(v) => setField("event_name", v)} placeholder="purchase" />
        </div>
      );
    case TagManagerDestination.TIKTOK_EVENTS:
      return (
        <div className="grid grid-cols-3 gap-2">
          <LabeledInput label="pixel_id" value={value.pixel_id ?? ""} onChange={(v) => setField("pixel_id", v)} />
          <LabeledInput label="access_token_ref" value={value.access_token_ref ?? ""} onChange={(v) => setField("access_token_ref", v)} placeholder="vibe/prod/…/<id>" />
          <LabeledInput label="event_name" value={value.event_name ?? ""} onChange={(v) => setField("event_name", v)} placeholder="Purchase" />
        </div>
      );
    case TagManagerDestination.CUSTOM_HTTP:
    default:
      return (
        <p className="text-xs text-muted-foreground">
          {tr("tm.http.hint")}
        </p>
      );
  }
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="space-y-1">
      <Label textCase="none" className="font-mono">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

// MonacoSnippetEditor was originally typed against the FE-mock string union.
// Bridge the canonical enum to that shape so the editor keeps picking the
// right syntax mode without us having to refactor the editor component yet.
function kindToMockShape(
  k: TagManagerSnippetKind,
): "GTM" | "PIXEL" | "POSTBACK" | "CUSTOM_JS" | "CUSTOM_HTML" {
  switch (k) {
    case TagManagerSnippetKind.GTM:
      return "GTM";
    case TagManagerSnippetKind.PIXEL:
      return "PIXEL";
    case TagManagerSnippetKind.POSTBACK:
      return "POSTBACK";
    case TagManagerSnippetKind.CUSTOM_HTML:
      return "CUSTOM_HTML";
    case TagManagerSnippetKind.CUSTOM_JS:
    default:
      return "CUSTOM_JS";
  }
}
